import React, { memo } from "react";

const SIZE_MAP = {
  xs: 14,
  sm: 18,
  md: 24,
  lg: 30,
  xl: 36,
} as const;

export type MaterialIconSize = keyof typeof SIZE_MAP;

export interface MaterialIconProps {
  path: string;
  size?: MaterialIconSize;
  color?: string;
  className?: string;
  onClick?: () => void;
  "aria-label"?: string;
  role?: string;
}

export const MaterialIcon = memo<MaterialIconProps>(
  ({
    path,
    size = "md",
    color,
    className = "",
    onClick,
    "aria-label": ariaLabel,
    role = "img",
  }) => {
    const px = SIZE_MAP[size];

    const handleClick = onClick
      ? (e: React.MouseEvent) => {
          e.preventDefault();
          onClick();
        }
      : undefined;

    // Default: currentColor (inherit from parent) - no need to pass color in buttons, etc.
    // When className provided: let Tailwind control color (text-subtext, etc.) - no style override.
    // When color prop provided: explicit CSS color (e.g. "currentColor", "#fff").
    const effectiveClassName = className.trim();
    const style =
      color !== undefined
        ? { color }
        : effectiveClassName
          ? undefined
          : { color: "currentColor" };

    // MDI paths are fill-based (not strokes). strokeWidth={1} matches our stroke icons’
    // nominal weight; a slight in-viewBox scale trims visual bulk so MDI sits closer to
    // 1px-outline custom icons in the same toolbar.
    const pathScale = 1;

    return (
      <svg
        width={px}
        height={px}
        viewBox="0 0 24 24"
        strokeWidth={1}
        className={effectiveClassName}
        style={style}
        onClick={handleClick}
        role={role}
        aria-label={ariaLabel}
        aria-hidden={!ariaLabel}
      >
        <g
          transform={`translate(12 12) scale(${pathScale}) translate(-12 -12)`}
        >
          <path d={path} fill="currentColor" stroke="none" />
        </g>
      </svg>
    );
  },
);

MaterialIcon.displayName = "MaterialIcon";
